import React, { useContext, useEffect, useRef } from "react";
import StatesDropdown from "components/common/StatesDropdown";
import { MobileContext } from "components/common";
import PropTypes from 'prop-types';

const autoCompletedAddressFormats = {
  street_number: "short_name",
  route: "long_name",
  locality: "long_name",
  sublocality_level_1: "long_name",
  administrative_area_level_1: "short_name",
  country: "long_name",
  postal_code: "short_name",
  subpremise: "short_name",
};

const AddressForm = ({
  street,
  city,
  state,
  zip,
  unitNumber,
  setStreet,
  setCity,
  setState,
  setZip,
  setUnitNumber,
  loading,
  errorMessage,
  SettingsInputContainer,
}) => {

  const addressInput = useRef(null);
  let autocomplete = useRef(null);
  const { isMobile } = useContext(MobileContext);

  const fill = () => {
    const place = autocomplete.getPlace();
    if (!place || !place.address_components) {
      return;
    }

    const n = {
      street_number: "",
      route: "",
      subpremise: "",
      locality: "",
      sublocality_level_1: "",
      administrative_area_level_1: "",
      country: "",
      postal_code: "",
    };

    for (let i = 0; i < place.address_components.length; i++) {
      const addressType = place.address_components[i].types[0];
      if (autoCompletedAddressFormats[addressType]) {
        const val =
          place.address_components[i][autoCompletedAddressFormats[addressType]];
        n[addressType] = val || "";
      }
    }

    const [a1, a2, c, s, z] = [
      n.street_number && n.route ? `${`${n.street_number} ${n.route}`}` : "",
      n.subpremise || "",
      n.locality || n.sublocality_level_1 || "",
      n.administrative_area_level_1 || "",
      n.postal_code || "",
    ];
    setStreet(a1);
    setUnitNumber(a2);
    setCity(c);
    setState(s);
    setZip(z);
  };

  const initAutocomplete = () => {
    autocomplete = new window.google.maps.places.Autocomplete(
      document.getElementById(`autocomplete-address`),
      { types: ["geocode"] },
    );
    autocomplete.setFields(["address_component"]);
    autocomplete.addListener("place_changed", () => {
      fill();
    });
  };

  useEffect(() => {
    initAutocomplete();
  }, []); //eslint-disable-line

  return (
    <div style={{ maxWidth: 400 }}>
      <SettingsInputContainer className="mb-2">
        <input
          id="autocomplete-address"
          value={street}
          placeholder="Address"
          type="text"
          className={`w-full outline-none ${errorMessage && "redBorder"} ${
            loading ? "text-gray-500" : ""
          }`}
          onChange={({ target: { value: v } }) => {
            setStreet(v);
          }}
          ref={addressInput}
          disabled={loading}
        />
      </SettingsInputContainer>
      <SettingsInputContainer className="mb-2">
        <input
          type="text"
          value={unitNumber}
          placeholder="Apt/Suite/Unit # (if applicable)"
          className={`w-full outline-none ${errorMessage && "redBorder"} ${
            loading ? "text-gray-500" : ""
          }`}
          onChange={({ target: { value: v } }) => {
            setUnitNumber(v);
          }}
          disabled={loading}
        />
      </SettingsInputContainer>
      <SettingsInputContainer className="mb-2">
        <input
          type="text"
          value={city}
          placeholder="City"
          className={`w-full outline-none ${errorMessage && "redBorder"} ${
            loading ? "text-gray-500" : ""
          }`}
          onChange={({ target: { value: v } }) => {
            setCity(v);
          }}
          disabled={loading}
        />
      </SettingsInputContainer>
      <div className={`flex ${isMobile ? "flex-col" : ""}`}>
        <div className="flex-1">
          <StatesDropdown
            didSelect={(v) => {
              setState(v.value);
            }}
            disabled={loading}
            defaultValue={state}
            SettingsInputContainer={SettingsInputContainer}
          />
        </div>
        <div className={`flex-1 ${isMobile ? "mt-2" : "pl-4"}`}>
          <SettingsInputContainer>
            <input
              type="text"
              value={zip}
              placeholder="Zip Code"
              className={`w-full outline-none ${errorMessage && "redBorder"} ${
                loading ? "text-gray-500" : ""
              }`}
              onChange={({ target: { value: v } }) => {
                setZip(v);
              }}
              disabled={loading}
            />
          </SettingsInputContainer>
        </div>
      </div>
    </div>
  );
};

AddressForm.defaultProps = {
  street: "",
  city: "",
  state: "",
  zip: "",
  unitNumber: "",
  setStreet: () => {},
  setCity: () => {},
  setState: () => {},
  setZip: () => {},
  setUnitNumber: () => {},
  loading: false,
  errorMessage: "",
};

AddressForm.propTypes = {
  street: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  unitNumber: PropTypes.string,
  setStreet: PropTypes.func,
  setCity: PropTypes.func,
  setState: PropTypes.func,
  setZip: PropTypes.func,
  setUnitNumber: PropTypes.func,
  loading: PropTypes.bool,
  errorMessage: PropTypes.string,
  SettingsInputContainer: PropTypes.func.isRequired,
};

export default AddressForm;
